import React from 'react';

import ALL_ROUTES from 'config/routes';
import {
  ProfileIcon,
  SubscriptionIcon,
  NotificationIcon,
  PaymentIcon,
  PasswordIcon,
  ContactIcon,
  InviteIcon,
  SupportIcon,
  PrivacyIcon,
  TosIcon,
  FaqIcon,
  Icon,
} from 'utils/icons';

// Helper function to get icon component for menu items
const getMenuIcon = (label, key) => {
  const iconMap = {
    'Duplicate Category': 'CloneIcon',
    'Delete Category': 'TrashCanIcon',
    'Delete Line Item': 'TrashCanIcon',
    'Duplicate Line Item': 'CloneIcon',
    'Duplicate Budget': 'CopyLink',
    'Duplicate Budget Structure': 'CopyLink',
    'Save as template...': 'FileLinesIcon',
    'Rename Budget': 'ICursorIcon',
    'Export PDF': 'FilePdfIcon',
    'Export CSV': 'file-csv',
    'Export JSON': 'FileLinesIcon',
    'Print Budget': 'PrinterIcon',
    'Cost Report…': 'FileLinesIcon',
    'Budget Settings': 'SlidersIcon',
    'Budget Fringes': 'PercentIcon',
    'Budget Globals': 'GlobeIcon',
    'Budget Tags': 'CategoryIcon',
    'Budget Credits': 'DollarSignIcon',
    'Delete Budget': 'TrashCanIcon',
    'Close Budget Tab': 'RectangleXmarkIcon',
    'Sharing (orange Button)': 'SlideshareIcon',
    'Share Budget': 'SlideshareIcon',
    'Manage Collaborators': 'GroupsIcon',
    'New Budget': 'FileLinesIcon',
    'Import Script': 'FilePdfIcon',
    'Import Budget': 'ArrowUpFromBracketIcon',
    Settings: 'GearIcon',
    Fringes: 'PercentIcon',
    Globals: 'GlobeIcon',
    Credits: 'DollarSignIcon',
    'User Settings': 'UserGearIcon',
    'User Fringes': 'PercentIcon',
    'User Globals': 'GlobeIcon',
    'Keyboard Shortcuts': 'KeyboardIcon',
    Website: 'GlobeIcon',
    'User Guide': 'MapIcon',
    'User Videos': 'CirclePlayIcon',
    'Privacy Policy': 'UserLockIcon',
    'Terms of Use': 'FileContractIcon',
    'Report an Issue': 'CommentIcon',
    'Move Items (my budgets)': 'FolderOpenIcon',
    'Share Budget (My Budgets)': 'ArrowUpFromBracketIcon',
    'Sort By  (Not Sorted)': 'SortIcon',
    'Sort By Text (A-Z)': 'ArrowDownAZIcon',
    'Sort By Text  (Z-A)': 'ArrowUpZAIcon',
    'Sort By Number (1-9)': 'ArrowDown19Icon',
    'Sort By Number (9-1)': 'ArrowUp91Icon',
    'Send Feedback': 'BullhornIcon',
    Edit: 'PenToSquareIcon',
    Cut: 'CutIcon',
    Copy: 'CopyIcon',
    Paste: 'PasteIcon',
    'Add Comment': 'CommentIcon',
    'Add New Fringe': 'PercentIcon',
    'Add New Global': 'GlobeIcon',
    'Show Account Numbers': 'VisibilityIcon',
    'Hide Account Numbers': 'VisibilityOffIcon',
    'toggle-account-numbers': 'VisibilityIcon',
  };

  // Try to match by label first, then by key
  const iconName = iconMap[label] || iconMap[key];
  if (iconName) {
    return (
      <Icon name={iconName} style={{ marginRight: '8px', width: '14px' }} />
    );
  }
  return null;
};
export const menuItems = [
  {
    label: 'My Budgets',
    key: 'myBudgets',
    show: true,
    path: 'budget-listing',
  },
  {
    label: 'File',
    key: 'file',
    show_for: [ALL_ROUTES.HOME_SCREEN],
    sub_menu_items: [
      {
        label: 'New Budget',
        key: 'new-budget',
        show: true,
        icon: getMenuIcon('New Budget', 'new-budget'),
      },
      {
        label: 'Import Script',
        key: 'import-script',
        show: true,
        icon: getMenuIcon('Import Script', 'import-script'),
      },
      {
        label: 'Import Budget',
        key: 'import-budget',
        show: true,
        icon: getMenuIcon('Import Budget', 'import-budget'),
      },
      {
        label: 'Open Budget',
        key: 'open-budget',
        show: false,
      },
      {
        label: 'Open Recent',
        key: 'open-recent',
        show: false,
      },
      {
        label: 'Duplicate Budget',
        key: 'duplicate-budget-with-values',
        show: true,
        icon: getMenuIcon('Duplicate Budget', 'duplicate-budget-with-values'),
      },
      {
        label: 'Duplicate Budget Structure',
        key: 'duplicate-budget',
        show: true,
        icon: getMenuIcon('Duplicate Budget', 'duplicate-budget'),
      },
      {
        label: 'Save as template...',
        key: 'save-as-template',
        show: true,
        icon: getMenuIcon('Save as template...', 'save-as-template'),
      },
      {
        label: 'Rename Budget',
        key: 'rename-budget',
        show: true,
        icon: getMenuIcon('Rename Budget', 'rename-budget'),
      },
      {
        label: 'Move to Folder',
        key: 'move-to-folder',
        show: false,
      },
      {
        type: 'divider',
        show: true,
      },
      {
        label: 'Cost Report…',
        key: 'cost-report',
        show: true,
        icon: getMenuIcon('Cost Report…', 'cost-report'),
      },
      {
        label: 'Export PDF',
        key: 'export-pdf',
        show: true,
        icon: getMenuIcon('Export PDF', 'export-pdf'),
      },
      {
        label: 'Export CSV',
        key: 'export-csv',
        show: true,
        icon: getMenuIcon('Export CSV', 'export-csv'),
      },
      {
        label: 'Export JSON',
        key: 'export-json',
        show: true,
        icon: getMenuIcon('Export JSON', 'export-json'),
      },
      {
        label: 'Print Budget',
        key: 'print-budget',
        show: true,
        icon: getMenuIcon('Print Budget', 'print-budget'),
      },
      {
        label: 'Print Setup',
        key: 'print-setup',
        show: false,
      },
      {
        type: 'divider',
      },
      {
        label: 'Budget Settings',
        key: 'budget-settings',
        show: true,
        icon: getMenuIcon('Budget Settings', 'budget-settings'),
      },
      {
        label: 'Budget Information',
        key: 'budget-information',
        show: false,
      },
      {
        type: 'divider',
        show: true,
      },
      {
        label: 'Archive Budget',
        key: 'archive-budget',
        show: false,
      },
      {
        label: 'Delete Budget',
        key: 'delete-budget',
        show: true,
        icon: getMenuIcon('Delete Budget', 'delete-budget'),
      },
    ],
  },
  {
    label: 'Edit',
    key: 'edit',
    show_for: [ALL_ROUTES.HOME_SCREEN],
    sub_menu_items: [
      {
        label: 'Undo',
        key: 'undo',
      },
      {
        label: 'Redo',
        key: 'redo',
      },
      {
        type: 'divider',
      },
      {
        label: 'Cut',
        key: 'cut',
        show: true,
        icon: getMenuIcon('Cut', 'cut'),
      },
      {
        label: 'Copy',
        key: 'copy',
        show: true,
        icon: getMenuIcon('Copy', 'copy'),
      },
      {
        label: 'Paste',
        key: 'paste',
        show: true,
        icon: getMenuIcon('Paste', 'paste'),
      },
      {
        label: 'Add Comment',
        key: 'add-comment',
        show: true,
        icon: getMenuIcon('Add Comment', 'add-comment'),
      },
      {
        label: 'Select all',
        key: 'select-all',
      },
      {
        label: 'Delete',
        key: 'delete',
      },
      {
        type: 'divider',
      },
      {
        label: 'Find',
        key: 'find',
      },
      {
        label: 'Find & Replace',
        key: 'find-replace',
      },
    ],
  },
  {
    label: 'Share',
    key: 'share',
    show_for: [ALL_ROUTES.HOME_SCREEN],
    sub_menu_items: [
      {
        label: 'Share Budget',
        key: 'share-budget',
        show: true,
        icon: getMenuIcon('Share Budget', 'share-budget'),
      },
      {
        label: 'Export to PDF',
        key: 'export-to-pdf',
        // type: 'link',
        show: true,
        icon: getMenuIcon('Export PDF', 'export-to-pdf'),
        // path: ALL_ROUTES.HOME_SCREEN_CHILDREN.PRINT_BUDGET,
      },
      {
        label: 'Email Budget as PDF',
        key: 'email-as-pdf',
        show: false,
      },
      {
        label: 'Email Share Link',
        key: 'email-share-link',
        show: false,
      },
      {
        label: 'Manage Collaborators',
        key: 'manage-collaborators',
        show: true,
        icon: getMenuIcon('Manage Collaborators', 'manage-collaborators'),
      },
      {
        type: 'divider',
        show: true,
      },
      {
        label: 'Print Budget',
        key: 'print-budget',
        show: true,
        icon: getMenuIcon('Print Budget', 'print-budget'),
      },
    ],
  },
  {
    label: 'View',
    key: 'view',
    show_for: [ALL_ROUTES.HOME_SCREEN],
    sub_menu_items: [
      {
        label: 'Show Account Numbers',
        key: 'toggle-account-numbers',
        show: true,
        icon: getMenuIcon('Show Account Numbers', 'toggle-account-numbers'),
      },
      {
        type: 'divider',
        show: true,
      },
      {
        label: 'Hide/Show Fringes',
        key: 'hide-show-fringes',
      },
      {
        label: 'Hide/Show Suggestions',
        key: 'hide-show-suggestions',
      },
      {
        label: 'Show AI Rate Card',
        key: 'hide-show-insights',
        show: true,
      },
      {
        label: 'Hide/Show tabs',
        key: 'hide-show-tabs',
      },
      {
        type: 'divider',
      },
      {
        label: 'Zoom In',
        key: 'zoom-in',
      },
      {
        label: 'Zoom Out',
        key: 'zoom-out',
      },
    ],
  },
  {
    label: 'Setup',
    key: 'setup',
    show: false,
    // show_for: [ALL_ROUTES.HOME_SCREEN],
    sub_menu_items: [
      {
        label: 'Rates',
        key: 'rates',
      },
      {
        label: 'Cast',
        key: 'cast',
      },
      {
        label: 'Crew',
        key: 'crew',
      },
      {
        label: 'Locations',
        key: 'locations',
      },
      {
        label: 'Shooting Days',
        key: 'shooting-days',
      },
    ],
  },
  {
    label: 'Add',
    key: 'add',
    show: false,
    //show_for: [ALL_ROUTES.HOME_SCREEN],
    sub_menu_items: [
      {
        label: 'Cast',
        key: 'cast',
      },
      {
        label: 'Crew',
        key: 'crew',
      },
      {
        label: 'Location',
        key: 'location',
      },
    ],
  },
  {
    label: 'Budget',
    key: 'budget_settings',
    show_for: [
      ALL_ROUTES.HOME_SCREEN,
      ALL_ROUTES.HOME_SCREEN_CHILDREN.PRINT_BUDGET,
      ALL_ROUTES.HOME_SCREEN_CHILDREN.GLOBALS_DETAILS,
      ALL_ROUTES.HOME_SCREEN_CHILDREN.TAGS_DETAILS,
      ALL_ROUTES.HOME_SCREEN_CHILDREN.CREDITS_DETAILS,
    ],
    sub_menu_items: [
      {
        label: 'Settings',
        key: 'budget-settings',
        show: true,
        icon: getMenuIcon('Settings', 'budget-settings'),
      },
      {
        label: 'Fringes',
        key: 'fringes',
        show: true,
        path: ALL_ROUTES.HOME_SCREEN_CHILDREN.FRINGES_DETAILS,
        type: 'link',
        instanceType: 'budget',
        icon: getMenuIcon('Fringes', 'fringes'),
      },
      {
        label: 'Globals',
        key: 'globals',
        show: true,
        path: ALL_ROUTES.HOME_SCREEN_CHILDREN.GLOBALS_DETAILS,
        type: 'link',
        instanceType: 'budget',
        icon: getMenuIcon('Globals', 'globals'),
      },
      {
        label: 'Tags',
        key: 'tags',
        show: true,
        path: ALL_ROUTES.HOME_SCREEN_CHILDREN.TAGS_DETAILS,
        type: 'link',
        instanceType: 'budget',
        icon: getMenuIcon('Budget Tags', 'budget-tags'),
      },
      {
        label: 'Credits',
        key: 'credits',
        show: true,
        path: ALL_ROUTES.HOME_SCREEN_CHILDREN.CREDITS_DETAILS,
        type: 'link',
        instanceType: 'budget',
        icon: getMenuIcon('Budget Credits', 'budget-credits'),
      },
    ],
  },
  {
    label: 'Settings',
    key: 'settings',
    show_for: [
      ALL_ROUTES.HOME_SCREEN,
      ALL_ROUTES.BUDGET_DETAILS,
      ALL_ROUTES.FRINGES_DETAILS,
      ALL_ROUTES.GLOBALS_DETAILS,
      ALL_ROUTES.CREDITS_DETAILS,
      ALL_ROUTES.HOME_SCREEN_CHILDREN.PRINT_BUDGET,
      ALL_ROUTES.HOME_SCREEN_CHILDREN.ACCOUNT_SETTINGS,
      ALL_ROUTES.ACCOUNT_SETTINGS_CHILDREN.PROFILE,
      ALL_ROUTES.ACCOUNT_SETTINGS_CHILDREN.SECURITY,
    ],
    sub_menu_items: [
      {
        label: 'User Settings',
        key: 'account-settings',
        type: 'link',
        show: true,
        path: ALL_ROUTES.HOME_SCREEN_CHILDREN.ACCOUNT_SETTINGS,
        icon: getMenuIcon('User Settings', 'account-settings'),
      },
      {
        label: 'User Fringes',
        key: 'user-settings-submenu',
        type: 'link',
        show: true,
        instanceType: 'user',
        path: ALL_ROUTES.HOME_SCREEN_CHILDREN.FRINGES_DETAILS,
        icon: getMenuIcon('User Fringes', 'user-settings-submenu'),
      },
      {
        label: 'User Globals',
        key: 'user-globals-submenu',
        type: 'link',
        show: true,
        instanceType: 'user',
        path: ALL_ROUTES.HOME_SCREEN_CHILDREN.GLOBALS_DETAILS,
        icon: getMenuIcon('User Globals', 'user-globals-submenu'),
      },
    ],
  },
  {
    label: 'Help',
    key: 'help',
    show: true,
    sub_menu_items: [
      {
        label: 'Keyboard Shortcuts',
        key: 'keyboard-shortcuts',
        show: true,
        type: 'link',
        path: 'https://www.cinebudgetpro.com/#/tips',
        target: '_blank',
        icon: getMenuIcon('Keyboard Shortcuts', 'keyboard-shortcuts'),
      },
      {
        label: 'Website',
        key: 'website',
        show: true,
        type: 'link',
        path: 'https://www.cinebudgetpro.com/',
        target: '_blank',
        icon: getMenuIcon('Website', 'website'),
      },
      {
        label: 'User Guide',
        key: 'user-guide',
        show: true,
        type: 'link',
        path: 'https://cuboid-hexaflexagon-kzxk.squarespace.com/learn',
        target: '_blank',
        icon: getMenuIcon('User Guide', 'user-guide'),
      },
      {
        label: 'User Videos',
        key: 'user-videos',
        show: true,
        type: 'link',
        path: 'https://www.youtube.com/embed/0hw3CzfS0YM?si=gXjGiB70eY_0ml-w',
        target: '_blank',
        icon: getMenuIcon('User Videos', 'user-videos'),
      },
      {
        type: 'divider',
        show: false,
      },
      {
        label: 'Privacy Policy',
        key: 'privacy-policy',
        show: true,
        type: 'link',
        path: 'https://www.cinebudgetpro.com/#/privacy',
        target: '_blank',
        icon: getMenuIcon('Privacy Policy', 'privacy-policy'),
      },
      {
        label: 'Terms of Use',
        key: 'terms-of-use',
        show: true,
        type: 'link',
        path: 'https://www.cinebudgetpro.com/#/terms',
        target: '_blank',
        icon: getMenuIcon('Terms of Use', 'terms-of-use'),
      },
      {
        type: 'divider',
        show: false,
      },
      {
        label: 'Report an Issue',
        key: 'report-issue',
        show: true,
        type: 'link',
        path: 'mailto: jon@cinebudgetpro.com',
        target: '_blank',
        icon: getMenuIcon('Report an Issue', 'report-issue'),
      },
    ],
  },
];

export const accountSettingsMenu = [
  {
    label: 'Profile',
    key: 'profile',
    path: ALL_ROUTES.ACCOUNT_SETTINGS_CHILDREN.PROFILE,
    icon: <ProfileIcon />,
  },
  {
    label: 'Payments',
    key: 'payments',
    path: ALL_ROUTES.ACCOUNT_SETTINGS_CHILDREN.PAYMENTS,
    icon: <PaymentIcon />,
    hidden: true,
  },
  {
    label: 'Subscription',
    key: 'subscription',
    path: ALL_ROUTES.ACCOUNT_SETTINGS_CHILDREN.SUBSCRIPTION,
    icon: <SubscriptionIcon />,
    hidden: true,
  },
  {
    label: 'Notifications',
    key: 'notifications',
    path: ALL_ROUTES.ACCOUNT_SETTINGS_CHILDREN.NOTIFICATIONS,
    icon: <NotificationIcon />,
    hidden: true,
  },
  {
    label: 'Security',
    key: 'security',
    path: ALL_ROUTES.ACCOUNT_SETTINGS_CHILDREN.SECURITY,
    icon: <PasswordIcon />,
    hidden: false,
  },
  {
    label: 'Contact Us',
    key: 'contact-us',
    path: ALL_ROUTES.ACCOUNT_SETTINGS_CHILDREN.CONTACT_US,
    icon: <ContactIcon />,
    hidden: true,
  },
  {
    label: 'Invite Friends',
    key: 'invite-friends',
    path: ALL_ROUTES.ACCOUNT_SETTINGS_CHILDREN.INVITE_FRIENDS,
    icon: <InviteIcon />,
    hidden: true,
  },
  {
    label: 'Support & Feedback',
    key: 'support-feedback',
    path: ALL_ROUTES.ACCOUNT_SETTINGS_CHILDREN.SUPPORT_FEEDBACK,
    icon: <SupportIcon />,
    hidden: true,
  },
  {
    label: 'Privacy Policy',
    key: 'privacy-policy',
    path: ALL_ROUTES.ACCOUNT_SETTINGS_CHILDREN.PRIVACY_POLICY,
    icon: <PrivacyIcon />,
    hidden: true,
  },
  {
    label: 'Terms & Conditions',
    key: 'terms-conditions',
    path: ALL_ROUTES.ACCOUNT_SETTINGS_CHILDREN.TERMS_CONDITIONS,
    icon: <TosIcon />,
    hidden: true,
  },
  {
    label: 'FAQs',
    key: 'faqs',
    path: ALL_ROUTES.ACCOUNT_SETTINGS_CHILDREN.FAQS,
    icon: <FaqIcon />,
    hidden: true,
  },
];

export const profileOptions = [
  { label: 'Student', value: '1' },
  { label: 'Content Creator', value: '2' },
  { label: 'Commercial Producer', value: '3' },
  { label: 'Guest', value: '4' },
  // { label: 'Admin', value: '5' },
];

export const AICP_TEMPLATE_NAMES = ['aicp', 'aicp budget'];

export const normalizeTemplateName = name =>
  name?.toString().trim().toLowerCase() || '';

export const isAicpTemplateName = name =>
  AICP_TEMPLATE_NAMES.includes(normalizeTemplateName(name));

export const getBudgetTemplateName = (budget, budgetTypes = []) =>
  budget?.template_type_name ||
  budgetTypes?.find(
    type => type?.id?.toString() === budget?.template_type?.toString()
  )?.name ||
  '';

export const PRINT_PRODUCTION_DETAILS_DEFAULT_TEMPLATE_NAMES = [
  'aicp',
  'commercial',
];

export const budgetMenu = [
  {
    label: 'Rename Budget',
    value: 'rename-budget',
    icon: getMenuIcon('Rename Budget', 'rename-budget'),
  },
  {
    label: 'Duplicate Budget',
    value: 'duplicate-budget-with-values',
    icon: getMenuIcon('Duplicate Budget', 'duplicate-budget-with-values'),
  },
  {
    label: 'Duplicate Budget Structure',
    value: 'duplicate-budget',
    icon: getMenuIcon('Duplicate Budget', 'duplicate-budget'),
  },
  {
    label: 'Save as template...',
    value: 'save-as-template',
    icon: getMenuIcon('Save as template...', 'save-as-template'),
  },
  {
    label: 'Export PDF',
    value: 'export-pdf',
    icon: getMenuIcon('Export PDF', 'export-pdf'),
  },
  {
    label: 'Export CSV',
    value: 'export-csv',
    icon: getMenuIcon('Export CSV', 'export-csv'),
  },
  {
    label: 'Export JSON',
    value: 'export-json',
    icon: getMenuIcon('Export JSON', 'export-json'),
  },
  {
    label: 'Print Budget',
    value: 'print-budget',
    icon: getMenuIcon('Print Budget', 'print-budget'),
  },
  {
    label: 'Budget Settings',
    value: 'budget-settings',
    icon: getMenuIcon('Budget Settings', 'budget-settings'),
  },
  {
    label: 'Budget Fringes',
    value: 'budget-fringes',
    icon: getMenuIcon('Budget Fringes', 'budget-fringes'),
  },
  {
    label: 'Budget Globals',
    value: 'budget-globals',
    icon: getMenuIcon('Budget Globals', 'budget-globals'),
  },
  {
    label: 'Budget Tags',
    value: 'budget-tags',
    icon: getMenuIcon('Budget Tags', 'budget-tags'),
  },
  {
    label: 'Budget Credits',
    value: 'budget-credits',
    icon: getMenuIcon('Budget Credits', 'budget-credits'),
  },
  //{ label: 'Budget Information', value: 'budget-information' },
  {
    label: 'Delete Budget',
    value: 'delete-budget',
    icon: getMenuIcon('Delete Budget', 'delete-budget'),
  },
  {
    label: 'Close Budget Tab',
    value: 'close-budget-tab',
    icon: getMenuIcon('Close Budget Tab', 'close-budget-tab'),
  },
];

export const profileMenu = [
  { label: 'Profile', value: 'profile' },
  { label: 'Log Out', value: 'logout' },
];

export const shootingLocations = [
  { label: 'Los Angeles, CA', value: 'Los Angeles, CA' },
  { label: 'Atlanta, GA', value: 'Atlanta, GA' },
  { label: 'New York, CA', value: 'New York, CA' },
  { label: 'San Francisco, CA', value: 'San Francisco, CA' },
  { label: 'London, UK', value: 'London, UK' },
  { label: 'Rome, Italy', value: 'Rome, Italy' },
  { label: 'Hawaii', value: 'Hawaii' },
  { label: 'Paris, France', value: 'Paris, France' },
  { label: 'Tokyo, Japan', value: 'Tokyo, Japan' },
  { label: 'Sydney, Australia', value: 'Sydney, Australia' },
  { label: 'Berlin, Germany', value: 'Berlin, Germany' },
  { label: 'New Orleans, LA', value: 'New Orleans, LA' },
  { label: 'Miami, FL', value: 'Miami, FL' },
  { label: 'Chicago, IL', value: 'Chicago, IL' },
  { label: 'Toronto, Canada', value: 'Toronto, Canada' },
  { label: 'Vancouver, Canada', value: 'Vancouver, Canada' },
  {
    label: 'Prague, Czechia (Czech Republic)',
    value: 'Prague, Czechia (Czech Republic)',
  },
  { label: 'Albuquerque, NM', value: 'Albuquerque, NM' },
  { label: 'Montreal, Canada', value: 'Montreal, Canada' },
  { label: 'Mumbai, India', value: 'Mumbai, India' },
  { label: 'Cape Town, South Africa', value: 'Cape Town, South Africa' },
  { label: 'Budapest, Hungary', value: 'Budapest, Hungary' },
  { label: 'Istanbul, Turkey', value: 'Istanbul, Turkey' },
  { label: 'Madrid, Spain', value: 'Madrid, Spain' },
  { label: 'Barcelona, Spain', value: 'Barcelona, Spain' },
  { label: 'Vienna, Austria', value: 'Vienna, Austria' },
  { label: 'Amsterdam, Netherlands', value: 'Amsterdam, Netherlands' },
  { label: 'Lisbon, Portugal', value: 'lisbon, Portugal' },
  { label: 'Hong Kong', value: 'Hong Kong' },
  { label: 'Seoul, South Korea', value: 'Seoul, South Korea' },
  { label: 'Beijing, China', value: 'Beijing, China' },
  { label: 'Buenos Aires, Argentina', value: 'Buenos Aires, Argentina' },
  { label: 'Sao Paulo, Brazil', value: 'Sao Paulo, Brazil' },
  { label: 'Rio de Janeiro, Brazil', value: 'Rio de Janeiro, Brazil' },
  { label: 'Bogota, Colombia', value: 'Bogota, Colombia' },
  { label: 'Lima, Peru', value: 'Lima, Peru' },
  { label: 'Mexico City, Mexico', value: 'Mexico City, Mexico' },
  { label: 'Santiago, Chile', value: 'Santiago, Chile' },
  { label: 'Guardalajara, Mexico', value: 'Guardalajara, Mexico' },
  { label: 'San Diego, CA', value: 'San Diego, CA' },
  { label: 'Nashville, TN', value: 'Nashville, TN' },
  { label: 'Austin, TX', value: 'Austin, TX' },
  { label: 'Denver, CO', value: 'Denver, CO' },
  { label: 'Phoenix, AZ', value: 'Phoenix, AZ' },
  { label: 'Morocco', value: 'Morocco' },
  { label: 'Jordan', value: 'Jordan' },
  { label: 'Melbourne, Australia', value: 'Melbourne, Australia' },
  { label: 'Boston, MA', value: 'Boston, MA' },
];

export const userActivity = {
  CONTEXTUAL_SUGGESTIONS: 1,
  BUDGET_EXPORTED: 2,
  BUDGET_PRINTED: 3,
};
